import React, { useState, useEffect, useRef } from 'react';
import 'cropperjs/dist/cropper.min.css';
import Cropper from 'react-cropper';
import { useTranslation } from '@blocs.i18n';
import Icon from '@minecraft.icon';
import Button from '@minecraft.button';
import { CoreTheme } from '@minecraft.themes';
import { WindowWidthContext } from '@minecraft.utils';
import { getEnvironment } from '@minecraft.environment';
import Loader from '@minecraft.loader';
import { gqlMedia } from 'components/GraphqlTypes';
import {
  CropperWrapper,
  CroppingToolsWrap,
  CroppingToolsContainer,
  StyledIconsWrap,
  StyledIcon,
  StyledIconRotated,
  StyledIconsWrapButton,
  StyledResetButton,
  CropperBlockWrapper,
} from './styles/ImageCropper';
import SCREEN_NAMES from './screenNames';

const { colors } = CoreTheme;

const environment = getEnvironment();
const cldDomain = environment?.CLD_DOMAIN;
const cloudinaryHotStorage = environment?.CN_HOT_CLOUDNAME;

type Props = {
  media: Pick<gqlMedia, 'fileKey' | 'transformation'>;
  alt?: string;
  responseType?: string;
  fileType?: string;
  showCroppingTools?: boolean;
  sideViewTools?: boolean | string;
  showCropTools?: boolean;
  crossOrigin?: 'anonymous' | 'use-credentials';
  scaleX?: number;
  scaleY?: number;
  dragMode?: 'crop' | 'move' | 'none';
  autoCropArea?: number;
  rotateAngle?: number;
  zoomRatio?: number;
  aspectRatio?: number;
  viewMode?: 0 | 1 | 2 | 3;
  minCropBoxWidth?: number;
  minCropBoxHeight?: number;
  fillColor?: string;
  rotateTo?: number;
  guides?: boolean;
  modal?: boolean; // for bg outside of crop box
  maxWidth?: number;
  maxHeight?: number;
  customRef?: React.RefObject<any>;
  setImageIsLoaded?: (isLoaded: boolean) => void;
  isChanged: boolean;
  setIsChanged: (isChanged: boolean) => void;
  movable: boolean;
  zoomOnWheel: boolean;
  zoomOnTouch: boolean;
};

const LANDSCAPE = 'LANDSCAPE';
const PORTRAIT = 'PORTRAIT';
const FREEMODE = 'FREEMODE';

const ImageCropper = ({
  rotateAngle,
  zoomRatio,
  media,
  alt,
  showCroppingTools,
  responseType,
  fileType,
  sideViewTools,
  showCropTools,
  customRef,
  setImageIsLoaded,
  isChanged,
  setIsChanged,
  ...other
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [activeMode, setActiveMode] = useState('');
  const mediaRef = useRef(null);
  const { useWidthData } = WindowWidthContext;
  const widthData = useWidthData();
  const { lessSm } = widthData;
  const imageSrc = `https://${cldDomain}/${cloudinaryHotStorage}/image/upload/${media?.fileKey}`;

  useEffect(() => {
    mediaRef.current = media;
    setImageIsLoaded && setImageIsLoaded(true);
  }, [media, setImageIsLoaded]);

  const freeMode = (): void => {
    customRef.current.setAspectRatio(null);
    setActiveMode(FREEMODE);
  };

  const aspectRatioLandscape = (): void => {
    setIsChanged(true);
    setActiveMode(LANDSCAPE);
    customRef.current.setAspectRatio(5 / 4);
  };

  const aspectRatioPortrait = (): void => {
    setIsChanged(true);
    setActiveMode(PORTRAIT);
    customRef.current.setAspectRatio(4 / 5);
  };

  const setRotationCropBox = (moveToCenter, scale, rotationValue): void => {
    const { cropper } = customRef.current;

    cropper.setCropBoxData({
      ...cropper.cropBoxData,
      ...moveToCenter,
    });

    cropper.rotate(rotationValue);

    cropper.setCropBoxData({
      ...cropper.cropBoxData,
      ...scale,
    });
  };

  const rotatePortrait = (rotationValue): void => {
    const { cropper } = customRef.current;
    const { width: imageWidth, height: imageHeight, rotate } = cropper.imageData;
    const isHorizontal = rotate === 90 || rotate === -90 || rotate === 270 || rotate === -270;
    let moveToCenter = {};
    let scale = {};

    if (isHorizontal) {
      moveToCenter = {
        width: imageWidth,
        height: imageHeight,
        left: (cropper.containerData.width - imageWidth) / 2,
        top: 0,
      };
      scale = {
        height: imageHeight,
        width: imageWidth,
        left: (cropper.containerData.width - imageWidth) / 2,
      };
    } else {
      moveToCenter = {
        height: imageWidth,
        top: (imageHeight - imageWidth) / 2,
      };
      scale = {
        left: 0,
        width: imageHeight,
      };
    }

    setRotationCropBox(moveToCenter, scale, rotationValue);
  };

  const rotateLandscape = (rotationValue): void => {
    const { cropper } = customRef.current;
    const { width: imageWidth, height: imageHeight, rotate } = cropper.imageData;

    const isVertical = rotate === 90 || rotate === -90 || rotate === 270 || rotate === -270;
    let moveToCenter = {};
    let scale = {};

    if (isVertical) {
      moveToCenter = {
        height: imageHeight,
        top: (imageWidth - imageHeight) / 2,
      };
      scale = {
        width: imageWidth,
        height: imageHeight,
      };
    } else {
      moveToCenter = {
        width: imageHeight,
        left: (imageWidth - imageHeight) / 2,
      };
      scale = {
        height: imageWidth,
        top: 0,
      };
    }

    setRotationCropBox(moveToCenter, scale, rotationValue);
  };

  const fitRotatedImageToContainer = (): void => {
    const { cropper } = customRef.current;
    const { width, height } = cropper.canvasData;
    const { naturalWidth } = cropper.imageData;
    const scale = naturalWidth / width;
    const cropBoxHeight = naturalWidth / scale;
    // set image in landscape size

    cropper.setCropBoxData({
      width: cropper.canvasData.width,
      height: cropBoxHeight,
      left: 0,
      top: 0,
    });
    // position it to center
    cropper.setCropBoxData({
      ...cropper.cropBoxData,
      top: (height - cropBoxHeight) / 2,
    });
    cropper.rotate(mediaRef.current.transformation.rotate);
    // stretch crop box to full width
    cropper.setCropBoxData({
      ...cropper.cropBoxData,
      width: cropper.canvasData.width,
    });
  };

  const updateCropBoxOnRotate = (direction): void => {
    const { cropper } = customRef.current;
    const rotationValue = direction === 'right' ? rotateAngle : -rotateAngle;
    // if we rotating image with full sized crop box on it -
    // set image to landscape/portrait position and adjust crop box to full image size
    const { width: canvasWidth, height: canvasHeight } = cropper.canvasData;
    const { aspectRatio } = cropper.imageData;
    const { width: cropBoxWidth, height: cropBoxHeight } = cropper.cropBoxData;

    // check if crop box is covering whole image

    if (cropBoxWidth >= canvasWidth || cropBoxHeight >= canvasHeight) {
      // rotate image to landscape/portrait mode
      if (aspectRatio < 1) {
        rotatePortrait(rotationValue);
      } else {
        rotateLandscape(rotationValue);
      }

      return;
    }

    // otherwise just rotate with default behavior
    cropper.rotate(rotationValue);
  };

  const rotateToRight = (): void => {
    setIsChanged(true);
    updateCropBoxOnRotate('right');
  };

  const rotateToLeft = (): void => {
    setIsChanged(true);
    updateCropBoxOnRotate('left');
  };

  const zoomIn = (): void => {
    customRef.current.zoom(zoomRatio);
  };

  const zoomOut = (): void => {
    customRef.current.zoom(-zoomRatio);
  };

  const adjustExistingTransformation = (mediaObj): void => {
    if (!mediaObj.transformation) return;

    const { cropper } = customRef.current;
    const { xAxis, yAxis, width, height, rotate } = mediaObj.transformation;

    cropper.setData({
      height,
      rotate,
      scaleX: 1,
      scaleY: 1,
      width,
      x: xAxis,
      y: yAxis,
    });
  };

  const handleReady = (): void => {
    setLoading(false);
    setIsChanged(false);
    setImageIsLoaded && setImageIsLoaded(false);
    const { transformation } = mediaRef.current;
    const scaleOnRotateValues = [0, 270, -270, 180, -180];

    if (transformation) {
      // if image is rotated - scale it to fit container
      if (!scaleOnRotateValues.includes(transformation.rotate)) {
        fitRotatedImageToContainer();
      }

      adjustExistingTransformation(mediaRef.current);
    }
  };

  const reset = (): void => {
    setIsChanged(false);
    setActiveMode('');
    customRef.current.reset();
    customRef.current.setAspectRatio(null);
    adjustExistingTransformation(media);
  };

  const cropEndHandle = (): void => {
    if (!isChanged) setIsChanged(true);
  };

  const zoomHandle = (): void => {
    if (!isChanged) setIsChanged(true);
  };

  const isLandscapeMode = activeMode === LANDSCAPE;
  const isPortraitMode = activeMode === PORTRAIT;
  const isFreeMode = activeMode === FREEMODE;
  const imageData = customRef?.current?.cropper?.getImageData();
  const showResetButtonRight = imageData?.aspectRatio > 1 && !lessSm;

  return (
    <CropperWrapper sideViewTools={sideViewTools} lessSm={lessSm}>
      {loading && <Loader />}
      {!loading && (
        <CroppingToolsWrap sideViewTools={sideViewTools}>
          <CroppingToolsContainer sideViewTools={sideViewTools}>
            {showCropTools && (
              <StyledIconsWrap sideViewTools={sideViewTools}>
                <StyledIconRotated
                  data-qa-id={`${SCREEN_NAMES.IMAGE_CROPPER}-5-4-icon`}
                  sideViewTools={sideViewTools}
                  onClick={aspectRatioLandscape}
                  isActive={isLandscapeMode}
                  aria-label={t('common:imageCropper.landscapeCrop')}
                >
                  <Icon color={isLandscapeMode ? colors.primary.white : colors.primary.primary} name="_5-4" />
                </StyledIconRotated>
                <StyledIconRotated
                  data-qa-id={`${SCREEN_NAMES.IMAGE_CROPPER}-4-5-icon`}
                  sideViewTools={sideViewTools}
                  onClick={aspectRatioPortrait}
                  isActive={isPortraitMode}
                  aria-label={t('common:imageCropper.portraitCrop')}
                >
                  <Icon color={isPortraitMode ? colors.primary.white : colors.primary.primary} name="_4-5" />
                </StyledIconRotated>
                <StyledIcon
                  data-qa-id={`${SCREEN_NAMES.IMAGE_CROPPER}-photo-tool-crop-icon`}
                  sideViewTools={sideViewTools}
                  onClick={freeMode}
                  isActive={isFreeMode}
                  aria-label={t('common:imageCropper.freeformCrop')}
                >
                  <Icon color={isFreeMode ? colors.primary.white : colors.primary.primary} name="free" />
                </StyledIcon>
              </StyledIconsWrap>
            )}
            <StyledIconsWrap sideViewTools={sideViewTools}>
              <StyledIconRotated
                data-qa-id={`${SCREEN_NAMES.IMAGE_CROPPER}-photo-tool-rotate-left-icon`}
                sideViewTools={sideViewTools}
                onClick={rotateToLeft}
                aria-label={t('common:imageCropper.rotateCounterClockwise')}
              >
                <Icon color={colors.primary.primary} name="photo-tool-rotate" />
              </StyledIconRotated>
              <StyledIcon
                data-qa-id={`${SCREEN_NAMES.IMAGE_CROPPER}-photo-tool-rotate-right-icon`}
                sideViewTools={sideViewTools}
                onClick={rotateToRight}
                aria-label={t('common:imageCropper.rotateClockwise')}
              >
                <Icon color={colors.primary.primary} name="photo-tool-rotate" />
              </StyledIcon>
            </StyledIconsWrap>
            <StyledIconsWrap sideViewTools={sideViewTools}>
              <StyledIcon
                data-qa-id={`${SCREEN_NAMES.IMAGE_CROPPER}-photo-tool-zoom-in-icon`}
                sideViewTools={sideViewTools}
                onClick={zoomIn}
                aria-label={t('common:imageCropper.zoomIn')}
              >
                <Icon color={colors.primary.primary} name="photo-tool-zoom-in" />
              </StyledIcon>
              <StyledIcon
                data-qa-id={`${SCREEN_NAMES.IMAGE_CROPPER}-photo-tool-zoom-out-icon`}
                sideViewTools={sideViewTools}
                onClick={zoomOut}
                aria-label={t('common:imageCropper.zoomOut')}
              >
                <Icon color={colors.primary.primary} name="photo-tool-zoom-out" />
              </StyledIcon>
            </StyledIconsWrap>
            {!showResetButtonRight && (
              <StyledIconsWrapButton>
                <Button
                  data-qa-id={`${SCREEN_NAMES.IMAGE_CROPPER}-reset-button`}
                  outlined="true"
                  onClick={reset}
                  disabled={!isChanged}
                >
                  {t('common:button.reset')}
                </Button>
              </StyledIconsWrapButton>
            )}
          </CroppingToolsContainer>
        </CroppingToolsWrap>
      )}
      <CropperBlockWrapper>
        <Cropper
          className="image-cropper"
          ref={customRef}
          ready={handleReady}
          zoom={zoomHandle}
          cropend={cropEndHandle}
          src={imageSrc}
          alt={alt}
          {...other}
        />
      </CropperBlockWrapper>

      {showResetButtonRight && (
        <StyledResetButton>
          <Button
            data-qa-id={`${SCREEN_NAMES.IMAGE_CROPPER}-reset-button`}
            outlined="true"
            disabled={!isChanged}
            onClick={reset}
            size="small"
          >
            {t('common:button.reset')}
          </Button>
        </StyledResetButton>
      )}
    </CropperWrapper>
  );
};

ImageCropper.defaultProps = {
  media: null,
  alt: 'image',
  responseType: '',
  fileType: '',
  showCropTools: false,
  showCroppingTools: false,
  sideViewTools: false,
  crossOrigin: 'anonymous',
  scaleX: 1,
  scaleY: 1,
  dragMode: 'move',
  movable: true,
  autoCropArea: 1,
  rotateAngle: 90,
  zoomRatio: 0.1,
  viewMode: 1,
  minCropBoxWidth: 10,
  minCropBoxHeight: 10,
  fillColor: '#fff',
  rotateTo: 0,
  guides: true,
  modal: true, // for bg outside of crop box
  maxWidth: 4069,
  maxHeight: 4069,
  zoomOnTouch: true,
  zoomOnWheel: true,
};

export default ImageCropper;
